<template>
  <div class="cashier-shell">
    <AppLoginWelcomeOverlay />

    <header class="cashier-navbar navbar navbar-expand-lg" data-tour="cashier-navbar">
      <div class="container-fluid cashier-navbar-inner">
        <router-link class="cashier-brand" :to="{ name: 'Login' }" title="Accueil de la gestion">
          <img src="../assets/logo.png" alt="" aria-hidden="true" />
          <span>
            <strong>Suite241</strong>
            <small>{{ brandSubtitle }}</small>
          </span>
        </router-link>

        <button
          class="navbar-toggler cashier-navbar-toggler"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#cashierNavbar"
          aria-controls="cashierNavbar"
          aria-expanded="false"
          aria-label="Afficher le menu caissier"
        >
          <span class="navbar-toggler-icon"></span>
        </button>

        <div id="cashierNavbar" class="collapse navbar-collapse cashier-navbar-collapse">
          <nav class="cashier-tabs navbar-nav" aria-label="Navigation caissier" data-tour="cashier-navigation">
            <router-link
              v-for="item in visibleNavItems"
              :key="item.name"
              class="cashier-tab"
              :class="{ active: isActive(item.active) }"
              :to="{ name: item.name }"
              :title="item.label"
            >
              <i :class="item.icon"></i>
              <span>{{ item.label }}</span>
            </router-link>
          </nav>

          <div class="cashier-navbar-actions">
            <time
              v-if="hasOpenCashRegisterSession && currentCashRegisterSession.opened_at"
              class="current-date-time cashier-date-time"
              :datetime="currentCashRegisterSession.opened_at"
              :title="`Session ouverte le ${$format.datetime(currentCashRegisterSession.opened_at, shopTimezone)}`"
            >
              <i class="fa-regular fa-clock" aria-hidden="true"></i>
              <span>Ouverte le {{ $format.datetime(currentCashRegisterSession.opened_at, shopTimezone) }}</span>
            </time>

            <!-- <span class="cashier-network-badge" :class="isOnline ? 'online' : 'offline'" data-tour="cashier-online">
              <i class="fa fa-circle"></i>
              {{ isOnline ? 'En ligne' : 'Hors ligne' }}
            </span> -->

            <button
              v-if="hasOpenCashRegisterSession"
              class="cashier-session-button"
              type="button"
              :disabled="isClosingCashRegisterSession || !isOnline"
              title="Se déconnecter de la session de caisse"
              aria-label="Se déconnecter de la session de caisse"
              @click="closeCashRegisterSession"
            >
              <i class="fa" :class="isClosingCashRegisterSession ? 'fa-spinner fa-spin' : 'fa-right-from-bracket'"></i>
              <span>{{ isClosingCashRegisterSession ? 'Fermeture...' : 'Déconnecter caisse' }}</span>
            </button>

            <SupportTicketButton variant="cashier" />

            <UserMenu
              variant="cashier"
              :show-role="false"
              :show-shop="false"
            />
          </div>
        </div>
      </div>
    </header>

    <main class="cashier-main" data-tour="page-content">
      <div class="container-fluid px-3 px-xl-4 py-4">
        <AppBreadcrumbs v-if="showBreadcrumbs" />
        <PageTutorialLauncher />
        <div v-if="staleSessionWarning" class="alert alert-warning py-2 mb-3" role="alert">
          <i class="fa fa-triangle-exclamation me-1" aria-hidden="true"></i>
          {{ staleSessionWarning }}
        </div>
        <div v-if="sessionCloseError" class="alert alert-danger py-2 mb-3">
          {{ sessionCloseError }}
        </div>
        <div v-if="sessionCloseSuccess" class="alert alert-success py-2 mb-3">
          {{ sessionCloseSuccess }}
        </div>
        <div v-if="requiresSiteSelection" class="cashier-site-gate">
          <i class="fa fa-store"></i>
          <strong>Choix du site requis</strong>
          <span>Sélectionnez le site de caisse pour continuer.</span>
        </div>
        <slot v-else name="content"></slot>
      </div>
    </main>

    <footer class="cashier-footer">
      <span>&copy; {{ currentYear }} Suite241. Tous droits réservés.</span>
      <span>Version {{ appVersion }}</span>
    </footer>

    <PrimevueDialog
      v-model:visible="siteSelectionDialogVisible"
      :modal="true"
      :closable="false"
      :draggable="false"
      :style="{ width: 'min(520px, 94vw)' }"
      class="cashier-site-dialog"
    >
      <template #header>
        <h5 class="modal-title mb-0">Choisir le site de caisse</h5>
      </template>

      <div class="cashier-site-dialog-body">
        <p class="text-muted mb-3">
          Votre compte est affecté à plusieurs sites. Choisissez le site sur lequel vous démarrez le service.
        </p>

        <div class="cashier-site-options">
          <button
            v-for="shop in accessibleShops"
            :key="shop.id"
            type="button"
            class="cashier-site-option"
            :disabled="isSelectingShop"
            @click="selectCashierShop(shop)"
          >
            <span>
              <strong>{{ shop.name || shop.code || `Site #${shop.id}` }}</strong>
              <small v-if="shop.is_primary">Site principal</small>
              <small v-else>Site accessible</small>
            </span>
            <i class="fa" :class="isSelectingShop ? 'fa-spinner fa-spin' : 'fa-arrow-right'"></i>
          </button>
        </div>
      </div>
    </PrimevueDialog>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { monitorApiAvailability } from '@/services/apiAvailability';
import pkg from '../../package.json';
import AppLoginWelcomeOverlay from '@/components/AppLoginWelcomeOverlay.vue';
import UserMenu from '@/components/UserMenu.vue';
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue';
import PageTutorialLauncher from '@/components/PageTutorialLauncher.vue';
import SupportTicketButton from '@/components/SupportTicketButton.vue';
import { CASHIER_SITE_CHOICE_REQUIRED_KEY } from '@/store/modules/auth';
import { hasPermission } from '@/support/permissions';

const calendarDateValue = (value, timezone) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone || undefined,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));

    return Date.UTC(Number(values.year), Number(values.month) - 1, Number(values.day));
  } catch (error) {
    return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  }
};

export default defineComponent({
  name: 'CashierLayout',
  components: {
    AppLoginWelcomeOverlay,
    UserMenu,
    AppBreadcrumbs,
    PageTutorialLauncher,
    SupportTicketButton,
  },
  data() {
    return {
      appVersion: pkg.version || '1.0.0',
      currentYear: new Date().getFullYear(),
      isOnline: true,
      stopApiAvailability: null,
      siteSelectionDialogVisible: false,
      siteChoiceRequired: window.localStorage.getItem(CASHIER_SITE_CHOICE_REQUIRED_KEY) === '1',
      isSelectingShop: false,
      isClosingCashRegisterSession: false,
      sessionCloseError: '',
      sessionCloseSuccess: '',
      sessionAgeReference: Date.now(),
      sessionAgeTimerId: null,
      navItems: [
        { name: 'CashierSales', module: 'sales', label: 'Caisse', icon: 'fa fa-cart-shopping', active: ['CashierSales'] },
        { name: 'CashierInventories', module: 'inventories', label: 'Inventaires', icon: 'fa fa-cubes-stacked', active: ['CashierInventories', 'CashierInventoryCount'] },
        { name: 'CashierEndOfDay', module: 'cash_counts', label: 'Fin journée', icon: 'fa fa-calendar-check', active: ['CashierEndOfDay'] },
        { name: 'Expenses', module: 'expenses', permission: 'expenses.view', label: 'Dépenses', icon: 'fa fa-receipt', active: ['Expenses', 'ExpenseShow', 'ExpenseEdit', 'ExpensesReport'] },
      ],
    };
  },
  computed: {
    visibleNavItems() {
      return this.navItems.filter((item) => this.$store.getters['runtime/moduleEnabled'](item.module)
        && (!item.permission || hasPermission(this.currentUser, item.permission)));
    },
    shopName() {
      return this.$store.getters['shop/shopName'];
    },
    currentUser() {
      return this.$store.getters['auth/currentUser'] || {};
    },
    accessibleShops() {
      const shops = Array.isArray(this.currentUser?.accessible_shops)
        ? this.currentUser.accessible_shops
        : [];

      if (shops.length) {
        return shops
          .filter((shop) => Number(shop?.id) > 0)
          .map((shop) => ({
            ...shop,
            id: Number(shop.id),
          }));
      }

      if (this.currentUser?.shop_id) {
        return [{
          id: Number(this.currentUser.shop_id),
          name: this.currentUser.shop?.name || null,
          code: this.currentUser.shop?.code || null,
          is_primary: true,
        }];
      }

      return [];
    },
    currentShopId() {
      return Number(this.$store.getters['shop/currentShopId'] || 0) || null;
    },
    currentCashRegisterSession() {
      return this.$store.getters['cashRegister/currentSession'];
    },
    shopTimezone() {
      return this.$store.getters['shop/timezone'] || undefined;
    },
    hasOpenCashRegisterSession() {
      return this.currentCashRegisterSession?.status === 'open';
    },
    staleSessionDays() {
      if (!this.hasOpenCashRegisterSession || !this.currentCashRegisterSession?.opened_at) {
        return 0;
      }

      const openedDate = calendarDateValue(this.currentCashRegisterSession.opened_at, this.shopTimezone);
      const currentDate = calendarDateValue(new Date(this.sessionAgeReference), this.shopTimezone);

      if (openedDate === null || currentDate === null) return 0;

      return Math.max(0, Math.floor((currentDate - openedDate) / 86400000));
    },
    staleSessionWarning() {
      if (this.staleSessionDays < 1) return '';

      const unit = this.staleSessionDays === 1 ? 'jour' : 'jours';
      const openedAt = this.$format.datetime(this.currentCashRegisterSession.opened_at, this.shopTimezone);

      return `Cette session de caisse n’a pas été clôturée depuis ${this.staleSessionDays} ${unit}. Ouverte le ${openedAt}.`;
    },
    hasMultipleAccessibleShops() {
      return this.currentUser?.role === 'caissier' && this.accessibleShops.length > 1;
    },
    currentShopIsAccessible() {
      return Boolean(
        this.currentShopId && this.accessibleShops.some((shop) => Number(shop.id) === this.currentShopId)
      );
    },
    canContinueOpenSessionOnCurrentShop() {
      return Boolean(
        this.hasOpenCashRegisterSession
        && this.currentShopId
        && Number(this.currentCashRegisterSession?.shop_id) === this.currentShopId
      );
    },
    requiresSiteSelection() {
      return this.hasMultipleAccessibleShops
        && (this.siteChoiceRequired || !this.currentShopIsAccessible)
        && !this.canContinueOpenSessionOnCurrentShop;
    },
    brandSubtitle() {
      return this.shopName || 'Stock';
    },
    showBreadcrumbs() {
      return !['CashierSales', 'CashierEndOfDay'].includes(String(this.$route.name));
    },
  },
  mounted() {
    this.stopApiAvailability = monitorApiAvailability((available) => {
      const recovered = available && !this.isOnline;
      this.isOnline = available;
      if (recovered) void this.handleOnline();
    });
    this.sessionAgeTimerId = window.setInterval(() => {
      this.sessionAgeReference = Date.now();
    }, 60000);
    this.syncCashierShopSelection();
  },
  beforeUnmount() {
    this.stopApiAvailability?.();
    if (this.sessionAgeTimerId) {
      window.clearInterval(this.sessionAgeTimerId);
    }
  },
  watch: {
    accessibleShops: {
      deep: true,
      handler() {
        this.syncCashierShopSelection();
      },
    },
    currentShopId() {
      this.syncCashierShopSelection();
    },
    hasOpenCashRegisterSession() {
      this.syncCashierShopSelection();
    },
  },
  methods: {
    isActive(names) {
      return names.includes(String(this.$route.name));
    },
    async syncCashierShopSelection() {
      if (this.currentUser?.role !== 'caissier') {
        this.siteChoiceRequired = false;
        this.siteSelectionDialogVisible = false;
        return;
      }

      if (this.canContinueOpenSessionOnCurrentShop) {
        this.siteChoiceRequired = false;
        this.siteSelectionDialogVisible = false;
        return;
      }

      if (this.accessibleShops.length === 1 && !this.currentShopIsAccessible && !this.isSelectingShop) {
        this.siteChoiceRequired = false;
        window.localStorage.removeItem(CASHIER_SITE_CHOICE_REQUIRED_KEY);
        await this.selectCashierShop(this.accessibleShops[0], { silent: true });
        return;
      }

      this.siteSelectionDialogVisible = this.requiresSiteSelection;
    },
    async selectCashierShop(shop, options = {}) {
      if (!shop?.id || this.isSelectingShop) return;

      this.isSelectingShop = true;
      this.sessionCloseError = '';

      try {
        await this.$store.dispatch('shop/changeSite', Number(shop.id));
        await this.$store.dispatch('cashRegister/fetchCurrent');
        window.localStorage.removeItem(CASHIER_SITE_CHOICE_REQUIRED_KEY);
        this.siteChoiceRequired = false;
        this.siteSelectionDialogVisible = false;
        window.dispatchEvent(new CustomEvent('suite241:cashier-shop-selected'));
      } catch (error) {
        if (!options.silent) {
          const errors = error.response?.data?.errors;
          this.sessionCloseError = errors
            ? Object.values(errors).flat().join(' ')
            : 'Impossible de sélectionner ce site.';
        }
      } finally {
        this.isSelectingShop = false;
      }
    },
    async closeCashRegisterSession() {
      if (this.isClosingCashRegisterSession) return;

      const confirmed = await this.$confirmAction(
        'Fermer votre session de caisse maintenant ? Vous ne pourrez plus enregistrer de ventes sur cette session, mais vous pourrez faire le billetage ensuite depuis la page Fin de journée.'
      );

      if (!confirmed) return;

      this.isClosingCashRegisterSession = true;
      this.sessionCloseError = '';
      this.sessionCloseSuccess = '';

      try {
        await this.$store.dispatch('cashRegister/closeCurrent');
        this.sessionCloseSuccess = 'Session de caisse fermée.';
        window.dispatchEvent(new CustomEvent('suite241:cash-register-session-closed'));

        if (this.$route.name !== 'CashierSales') {
          await this.$router.push({ name: 'CashierSales' });
        }
      } catch (error) {
        const errors = error.response?.data?.errors;
        this.sessionCloseError = errors
          ? Object.values(errors).flat().join(' ')
          : 'Impossible de fermer la session de caisse.';
      } finally {
        this.isClosingCashRegisterSession = false;
      }
    },
    handleOnline() {
      this.isOnline = true;
    },
  },
});
</script>

<style scoped>
.cashier-shell {
  background:
    radial-gradient(circle at 18% 0%, rgba(37, 99, 235, 0.08), transparent 28rem),
    #f6f8fc;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.cashier-navbar {
  background:
    radial-gradient(circle at 10% 0%, rgba(24, 148, 112, 0.24), transparent 26rem),
    linear-gradient(105deg, #002a24 0%, #00513f 32%, #003b32 52%, #111514 68%, #000000 100%);
  box-shadow: 0 18px 42px rgba(0, 95, 79, 0.22);
  min-height: 76px;
  padding: 0;
  position: sticky;
  top: 0;
  z-index: 1030;
}

.cashier-navbar-inner {
  align-items: center;
  display: flex;
  gap: 1.25rem;
  min-height: 76px;
  padding: 0 1.4rem;
}

.cashier-brand {
  align-items: center;
  color: #ffffff;
  display: inline-flex;
  gap: 0.7rem;
  min-width: 160px;
  text-decoration: none;
}

.cashier-brand img {
  height: 38px;
  width: 38px;
}

.cashier-brand span {
  display: flex;
  flex-direction: column;
  line-height: 1.05;
}

.cashier-brand strong {
  font-size: 1.15rem;
  font-weight: 900;
}

.cashier-brand small {
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.78rem;
  font-weight: 700;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cashier-navbar-toggler {
  border-color: rgba(255, 255, 255, 0.34);
  margin-left: auto;
}

.cashier-navbar-toggler:focus {
  box-shadow: 0 0 0 0.16rem rgba(58, 213, 165, 0.42);
}

.cashier-navbar-toggler .navbar-toggler-icon {
  filter: invert(1) grayscale(1) brightness(1.8);
}

.cashier-navbar-collapse {
  align-items: center;
  gap: 1rem;
}

.cashier-tabs {
  align-items: center;
  display: flex;
  flex: 1 1 auto;
  gap: 0.35rem;
  min-width: 0;
}

.cashier-tab {
  align-items: center;
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.68);
  display: inline-flex;
  font-size: 0.86rem;
  font-weight: 800;
  gap: 0.48rem;
  min-height: 44px;
  padding: 0.7rem 0.95rem;
  position: relative;
  text-decoration: none;
  white-space: nowrap;
}

.cashier-tab i {
  font-size: 1rem;
}

.cashier-tab:hover,
.cashier-tab:focus {
  background: rgba(255, 255, 255, 0.08);
  color: #ffffff;
}

.cashier-tab.active {
  background: rgba(255, 255, 255, 0.16);
  color: #ffffff;
}

.cashier-tab.active::after {
  background: #ffffff;
  border-radius: 999px 999px 0 0;
  bottom: -16px;
  content: "";
  height: 3px;
  left: 0.75rem;
  position: absolute;
  right: 0.75rem;
}

.cashier-navbar-actions {
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  gap: 0.75rem;
  margin-left: auto;
}

.current-date-time {
  align-items: center;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 999px;
  color: rgba(255, 255, 255, 0.86);
  display: inline-flex;
  font-size: 0.76rem;
  font-weight: 800;
  gap: 0.45rem;
  line-height: 1;
  min-height: 32px;
  padding: 0 0.72rem;
  white-space: nowrap;
}

.current-date-time i {
  font-size: 0.84rem;
}

.cashier-network-badge {
  align-items: center;
  border-radius: 999px;
  display: inline-flex;
  font-size: 0.75rem;
  font-weight: 800;
  gap: 0.4rem;
  padding: 0.35rem 0.7rem;
  white-space: nowrap;
}

.cashier-network-badge.online {
  background: rgba(34, 197, 94, 0.15);
  color: #86efac;
}

.cashier-network-badge.offline {
  background: rgba(239, 68, 68, 0.15);
  color: #fca5a5;
}

.cashier-network-badge i {
  font-size: 0.55rem;
}

.cashier-session-button {
  align-items: center;
  background: rgba(245, 158, 11, 0.14);
  border: 1px solid rgba(251, 191, 36, 0.42);
  border-radius: 8px;
  color: #fde68a;
  display: inline-flex;
  font-size: 0.78rem;
  font-weight: 900;
  gap: 0.45rem;
  min-height: 38px;
  padding: 0 0.75rem;
  transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;
  white-space: nowrap;
}

.cashier-session-button:hover,
.cashier-session-button:focus {
  background: rgba(245, 158, 11, 0.24);
  color: #ffffff;
  transform: translateY(-1px);
}

.cashier-session-button:disabled {
  cursor: not-allowed;
  opacity: 0.58;
  transform: none;
}

.cashier-main {
  flex: 1 1 auto;
  min-width: 0;
}

.cashier-site-gate {
  align-items: center;
  background: #ffffff;
  border: 1px solid rgba(15, 118, 110, 0.14);
  border-radius: 8px;
  box-shadow: 0 14px 32px rgba(15, 23, 42, 0.08);
  color: #0f172a;
  display: grid;
  gap: 0.35rem;
  justify-items: center;
  margin: 3rem auto;
  max-width: 520px;
  padding: 2rem;
  text-align: center;
}

.cashier-site-gate i {
  align-items: center;
  background: rgba(13, 148, 136, 0.1);
  border-radius: 999px;
  color: #0f766e;
  display: inline-flex;
  font-size: 1.6rem;
  height: 3.5rem;
  justify-content: center;
  width: 3.5rem;
}

.cashier-site-dialog-body {
  display: grid;
  gap: 0.5rem;
}

.cashier-site-options {
  display: grid;
  gap: 0.75rem;
}

.cashier-site-option {
  align-items: center;
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 8px;
  color: #0f172a;
  display: flex;
  justify-content: space-between;
  padding: 0.85rem 1rem;
  text-align: left;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
  width: 100%;
}

.cashier-site-option:hover,
.cashier-site-option:focus {
  border-color: rgba(13, 148, 136, 0.5);
  box-shadow: 0 10px 24px rgba(15, 118, 110, 0.12);
  transform: translateY(-1px);
}

.cashier-site-option:disabled {
  cursor: wait;
  opacity: 0.72;
  transform: none;
}

.cashier-site-option span {
  display: grid;
  gap: 0.2rem;
}

.cashier-site-option small {
  color: #64748b;
  font-weight: 700;
}

.cashier-footer {
  align-items: center;
  background: #ffffff;
  border-top: 1px solid #dfe6f1;
  color: #64748b;
  display: grid;
  font-size: 0.84rem;
  gap: 1rem;
  grid-template-columns: 1fr auto;
  min-height: 76px;
  padding: 1rem 2rem;
}

.cashier-footer span:nth-child(2) {
  justify-self: center;
}

@media (max-width: 1366px), (max-height: 820px) {
  .cashier-navbar {
    min-height: 58px;
  }

  .cashier-navbar-inner {
    gap: 0.85rem;
    min-height: 58px;
    padding: 0 1rem;
  }

  .cashier-brand {
    gap: 0.55rem;
    min-width: 132px;
  }

  .cashier-brand img {
    height: 30px;
    width: 30px;
  }

  .cashier-brand strong {
    font-size: 0.98rem;
  }

  .cashier-brand small {
    font-size: 0.68rem;
    max-width: 118px;
  }

  .cashier-tabs {
    gap: 0.2rem;
  }

  .cashier-tab {
    font-size: 0.76rem;
    gap: 0.38rem;
    min-height: 38px;
    padding: 0.45rem 0.68rem;
  }

  .cashier-tab i {
    font-size: 0.9rem;
  }

  .cashier-tab.active::after {
    bottom: -10px;
    height: 2px;
    left: 0.55rem;
    right: 0.55rem;
  }

  .cashier-navbar-actions {
    gap: 0.45rem;
  }

  .cashier-date-time {
    font-size: 0.64rem;
    min-height: 28px;
    padding: 0 0.48rem;
  }

  .cashier-network-badge {
    font-size: 0.66rem;
    padding: 0.25rem 0.5rem;
  }

  .cashier-session-button {
    font-size: 0.68rem;
    min-height: 32px;
    padding: 0 0.55rem;
  }

  .cashier-main .container-fluid {
    padding-bottom: 1rem !important;
    padding-top: 1rem !important;
  }

  .cashier-main :deep(.card-body) {
    padding: 0.85rem;
  }

  .cashier-main :deep(.card-header),
  .cashier-main :deep(.card-footer) {
    padding: 0.65rem 0.85rem;
  }

  .cashier-main :deep(.btn) {
    font-size: 0.78rem;
    padding: 0.35rem 0.62rem;
  }

  .cashier-main :deep(.form-control),
  .cashier-main :deep(.form-select) {
    font-size: 0.8rem;
    min-height: 36px;
    padding: 0.35rem 0.55rem;
  }

  .cashier-main :deep(h1) {
    font-size: 1.45rem;
  }

  .cashier-main :deep(h2) {
    font-size: 1.15rem;
  }

  .cashier-main :deep(h3),
  .cashier-main :deep(h4),
  .cashier-main :deep(h5) {
    font-size: 0.98rem;
  }

  .cashier-main :deep(p),
  .cashier-main :deep(label),
  .cashier-main :deep(.table),
  .cashier-main :deep(.text-muted),
  .cashier-main :deep(.small),
  .cashier-main :deep(small) {
    font-size: 0.78rem;
  }

  .cashier-footer {
    font-size: 0.72rem;
    min-height: 54px;
    padding: 0.55rem 1.2rem;
  }

}

@media (max-width: 991.98px) {
  .cashier-navbar-inner {
    padding: 0.8rem 1rem;
  }

  .cashier-navbar-collapse {
    align-items: stretch;
    flex-basis: 100%;
    padding-top: 0.85rem;
  }

  .cashier-tabs {
    align-items: stretch;
    display: grid;
    gap: 0.45rem;
    width: 100%;
  }

  .cashier-tab {
    justify-content: flex-start;
    width: 100%;
  }

  .cashier-tab.active::after {
    bottom: 0.45rem;
    left: auto;
    right: 0.65rem;
    top: 0.45rem;
    width: 3px;
    height: auto;
  }

  .cashier-navbar-actions {
    border-top: 1px solid rgba(255, 255, 255, 0.12);
    flex-wrap: wrap;
    justify-content: space-between;
    margin-left: 0;
    padding-top: 0.85rem;
    width: 100%;
  }

  .cashier-footer {
    grid-template-columns: 1fr;
    justify-items: start;
    padding: 1rem;
  }

  .cashier-footer span:nth-child(2) {
    justify-self: start;
  }
}

@media (max-width: 575.98px) {
  .cashier-brand {
    min-width: 0;
  }

  .cashier-brand small {
    max-width: 120px;
  }

  .cashier-network-badge {
    display: none;
  }
}
</style>
